import { computed, isReadonly, isRef, MaybeRefOrGetter, Ref, ref, toValue, watch } from 'vue'

export function clamp(value: number, min: number | null = null, max: number | null = null): number {
  if (min !== null) {
    value = Math.max(value, min)
  }
  if (max !== null) {
    value = Math.min(value, max)
  }
  return value
}

export function mapToDomain(
  value: number,
  inputDomain: [number, number],
  outputDomain: [number, number],
) {
  const [x1, y1] = inputDomain
  const [x2, y2] = outputDomain
  value = clamp(value, x1, y1)
  return ((value - x1) * (y2 - x2)) / (y1 - x1) + x2
}

export function useClamp(
  value: MaybeRefOrGetter<number | string>,
  options: { min?: number; max?: number; sync?: boolean },
) {
  const number = ref(normalizeValue(toValue(value)))

  function normalizeValue(value: number | string) {
    value = typeof value === 'string' ? parseFloat(value) : value
    if (Number.isNaN(value)) {
      value = toValue(value)
    }
    return clamp(value, options?.min ?? null, options?.max ?? null)
  }

  if (options?.sync) {
    if (isRef(value) && !isReadonly(value)) {
      const _value = value as Ref<number | string>
      watch(number, (newValue) => (_value.value = newValue), { immediate: true })
    } else {
      console.warn('Passed non writable value to useClamp, but sync was enabled.', value)
    }
  }

  return computed({
    get: () => number.value,
    set(value: number | string) {
      const newValue = normalizeValue(value)
      if (newValue !== number.value) {
        number.value = newValue
      }
    },
  })
}

export function areNumbersClose(v1: number, v2: number, deviation = 0.01): boolean {
  return Math.abs(v1 - v2) <= deviation
}
